import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import InviteLink from "@components/common/InviteLink";
import { useCurrentUser } from "@hooks";
import { REDEEM_CREDITS } from "@src/graphql/mutations/Referral";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";

import { AccountItemBlock } from "./AccountItem";
import { DSButton, DSLinkButton } from "../design-system/molecules";
import { DSModal } from "../design-system/organisms";

export default function ReferralSettings() {
  const { releaseWebPayItFurwardReferrals } = useFlags();
  const { user, refetch } = useCurrentUser();
  const [redeemAmount, setRedeemAmount] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [redeemCredits, { loading: redeeming }] =
    useMutation(REDEEM_CREDITS);

  const referral = user?.referral;
  const isProUser = user?.account?.isSubscribed;
  // Convert cents to dollars for display and calculations
  const creditsBalanceCents = referral?.creditsBalanceCents || 0;
  const creditsBalance = creditsBalanceCents / 100;
  const totalCreditsEarned =
    (referral?.totalCreditsEarnedCents || 0) / 100;
  const totalCreditsRedeemed =
    (referral?.totalCreditsRedeemedCents || 0) / 100;
  const minRedemptionCents =
    referral?.minRedemptionAmountCents || 2500;
  const minRedemption = minRedemptionCents / 100;
  const canRedeem = referral?.canRedeem;
  const parsedAmount = parseFloat(redeemAmount) || 0;
  const isValidAmount =
    parsedAmount >= minRedemption && parsedAmount <= creditsBalance;

  // Feature flag: hide entire component if not enabled
  if (!releaseWebPayItFurwardReferrals) {
    return null;
  }

  const formatCurrency = (amount) => {
    const num = parseFloat(amount || "0");
    return `$${num.toFixed(2)}`;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty, or valid decimal numbers
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setRedeemAmount(value);
    }
  };

  const handleMaxClick = () => {
    setRedeemAmount(creditsBalance.toFixed(2));
  };

  const handleRedeem = async () => {
    if (!isValidAmount) {
      alert(
        "error",
        `Please enter an amount between $${minRedemption.toFixed(
          2,
        )} and $${creditsBalance.toFixed(2)}`,
      );
      return;
    }

    try {
      // Convert dollars to cents (integer) to avoid floating-point precision issues
      const amountCents = Math.round(parsedAmount * 100);
      const result = await redeemCredits({
        variables: {
          amountCents,
          recipientName: user?.firstName + " " + user?.lastName,
          recipientEmail: user?.email,
        },
      });

      const { success, message } = result.data.redeemCredits;

      if (success) {
        alert(
          "success",
          `$${parsedAmount.toFixed(
            2,
          )} redeemed! Check your email for the prepaid card - we usually approve within 24 hours.`,
        );
        setRedeemAmount("");
        refetch();
      } else {
        alert("error", message);
      }
    } catch {
      alert("error", "Failed to redeem credits. Please try again.");
    }
    setShowConfirmModal(false);
  };

  return (
    <div className="flex-1 flex flex-col gap-md">
      <div className="flex-1 divide-y divide-primary">
        {/* Program Introduction */}
        <div className="pb-md">
          <p className="text-base text-primary">
            <span className="font-semibold">
              You earn $200. Your friend earns $200.
            </span>{" "}
            <span className="text-secondary">
              Refer a colleague to Scribenote Pro and use your credits
              to help pets whose owners can't afford care.
            </span>{" "}
            <DSLinkButton
              href="https://scribenote.com/pay-it-furward"
              target="_blank"
              rel="noopener noreferrer"
              dataTestId="pay-it-furward-learn-more-link"
            >
              Learn More →
            </DSLinkButton>
          </p>
        </div>

        <AccountItemBlock
          title="Refer a Friend:"
          desc="Share your unique code with your friends verbally, or click to copy the link. They can enter it when they sign up!"
        >
          {isProUser ? (
            <InviteLink
              referralCode={referral?.referralCode}
              userName={user?.firstName || ""}
            />
          ) : (
            <div className="py-lg">
              <div className="bg-warning-subtle border border-warning rounded-standard p-lg">
                <p className="text-warning font-medium">
                  🐾 Upgrade to Scribenote Pro to start referring
                  friends and earning credits!
                </p>
              </div>
            </div>
          )}
        </AccountItemBlock>

        {/* Credits Summary */}
        <div className="py-md space-y-md">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-md">
            <div className="bg-success-subtle rounded-standard p-lg text-center">
              <p className="text-secondary">Available Balance</p>
              <p className="text-2xl font-bold text-success">
                {formatCurrency(creditsBalance)}
              </p>
            </div>
            <div className="bg-secondary rounded-standard p-lg text-center">
              <p className="text-secondary">Total Earned</p>
              <p className="text-2xl font-bold text-brand">
                {formatCurrency(totalCreditsEarned)}
              </p>
            </div>
            <div className="bg-secondary rounded-standard p-lg text-center">
              <p className="text-secondary">
                Total Redeemed to Help Pets
              </p>
              <p className="text-2xl font-bold text-brand">
                {formatCurrency(totalCreditsRedeemed)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-md">
            <div className="bg-secondary rounded-standard p-lg text-center">
              <p className="text-secondary">
                Friends Referred Who Signed Up
              </p>
              <p className="text-2xl font-bold text-brand">
                {referral?.numReferredSignups || 0}
              </p>
            </div>
            <div className="bg-secondary rounded-standard p-lg text-center">
              <p className="text-secondary">
                Friends Referred Who Upgraded to Pro
              </p>
              <p className="text-2xl font-bold text-brand">
                {referral?.numPaidReferrals || 0}
              </p>
            </div>
          </div>
        </div>

        {referral?.hasReferrer && (
          <div className="py-lg">
            <div className="bg-success-subtle border border-success rounded-standard p-lg">
              <p className="text-success font-medium">
                ✓ You were referred by a friend!{" "}
                {isProUser
                  ? ""
                  : "You'll receive credits when you upgrade to Pro!"}
              </p>
            </div>
          </div>
        )}

        {/* Redeem Credits Section */}
        {creditsBalance > 0 && (
          <AccountItemBlock
            title="Redeem for Patient Care:"
            desc={`You have ${formatCurrency(
              creditsBalance,
            )} available to help pets in need. Redeem your credits for a prepaid Visa card.`}
          >
            <div className="flex items-center gap-sm">
              <div className="relative">
                <span className="absolute left-md top-1/2 -translate-y-1/2 text-secondary">
                  $
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={redeemAmount}
                  onChange={handleAmountChange}
                  placeholder={minRedemption.toFixed(2)}
                  className="w-24 pl-lg pr-sm py-xs text-sm rounded-standard border border-secondary bg-input text-primary placeholder:text-placeholder focus:outline-hidden focus:ring-2 focus:ring-brand"
                />
              </div>
              <DSLinkButton
                onClick={handleMaxClick}
                dataTestId="redeem-max-button"
              >
                Max
              </DSLinkButton>
              <DSButton
                onClick={() => setShowConfirmModal(true)}
                disabled={!canRedeem || !isValidAmount || redeeming}
                dataTestId="redeem-credits-button"
              >
                {redeeming ? "..." : "Redeem"}
              </DSButton>
            </div>
            {parsedAmount > 0 && !isValidAmount && (
              <p className="text-sm text-danger mt-sm">
                Amount must be between ${minRedemption.toFixed(2)} and
                ${creditsBalance.toFixed(2)}
              </p>
            )}
          </AccountItemBlock>
        )}
      </div>

      <DSModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Redemption"
        dataTestId="redeem-confirm-modal"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShowConfirmModal(false),
          },
          {
            label: redeeming ? "Redeeming..." : "Confirm",
            variant: "primary",
            onClick: handleRedeem,
            disabled: redeeming,
          },
        ]}
      >
        <div className="space-y-md">
          <p className="text-primary">
            You're about to redeem{" "}
            <strong>${parsedAmount.toFixed(2)}</strong> in Pay It
            Furward credits.
          </p>
          <p className="text-primary">
            A prepaid Visa card will be sent to your email (
            <strong>{user?.email}</strong>).
          </p>
          <p className="text-sm text-secondary">
            Use this card to help cover the cost of care for any pet
            in need!
          </p>
        </div>
      </DSModal>
    </div>
  );
}
